import { HttpClient } from "./HttpClient";
import type {
  AuthProvider,
  Credentials,
  EmailConfirmationHandle,
  EventEmitter,
  RegisterCredentials,
  RegistrableAuthProvider,
  User,
} from "../types";

function isRegistrable(provider: AuthProvider): provider is RegistrableAuthProvider {
  const p = provider as Partial<RegistrableAuthProvider>;
  return (
    typeof p.register === "function" &&
    typeof p.requestEmailConfirmation === "function" &&
    typeof p.confirmEmailConfirmation === "function"
  );
}

export interface AuthClientOptions {
  provider: AuthProvider;
  /** Base URL for the *host application's* own API — separate from the auth backend itself. */
  apiBaseUrl?: string;
  defaultTimeoutMs?: number;
}

/**
 * Wraps any AuthProvider with a ready-to-use HttpClient so the host
 * application never has to wire token injection/refresh itself. This is
 * the object returned by `useAuth()` as `auth`, and `auth.client.request(...)`
 * is what application code should use instead of raw `fetch()`.
 */
export class AuthClient {
  readonly provider: AuthProvider;
  readonly client: HttpClient;

  constructor(opts: AuthClientOptions) {
    this.provider = opts.provider;
    this.client = new HttpClient({
      baseUrl: opts.apiBaseUrl ?? "",
      defaultTimeoutMs: opts.defaultTimeoutMs,
      getAccessToken: () => this.provider.getAccessToken(),
      refreshAccessToken: async () => {
        try {
          await this.provider.refresh();
          return await this.provider.getAccessToken();
        } catch {
          return null;
        }
      },
      onAuthFailure: () => {
        void this.provider.logout();
      },
    });
  }

  initialize(): Promise<void> {
    return this.provider.initialize();
  }

  login(credentials: Credentials): Promise<void> {
    return this.provider.login(credentials);
  }

  logout(): Promise<void> {
    return this.provider.logout();
  }

  refresh(): Promise<void> {
    return this.provider.refresh();
  }

  getCurrentUser(): Promise<User | null> {
    return this.provider.getCurrentUser();
  }

  isAuthenticated(): boolean {
    return this.provider.isAuthenticated();
  }

  /** True when the configured provider supports `register`/email confirmation. */
  supportsRegistration(): boolean {
    return isRegistrable(this.provider);
  }

  register(credentials: RegisterCredentials): Promise<void> {
    if (!isRegistrable(this.provider)) {
      throw new Error("The configured auth provider does not support registration.");
    }
    return this.provider.register(credentials);
  }

  requestEmailConfirmation(email: string): Promise<EmailConfirmationHandle> {
    if (!isRegistrable(this.provider)) {
      throw new Error("The configured auth provider does not support registration.");
    }
    return this.provider.requestEmailConfirmation(email);
  }

  confirmEmailConfirmation(nonce: string, code: string): Promise<void> {
    if (!isRegistrable(this.provider)) {
      throw new Error("The configured auth provider does not support registration.");
    }
    return this.provider.confirmEmailConfirmation(nonce, code);
  }

  get events(): EventEmitter {
    return this.provider.events;
  }
}
